import type { BookedVisit } from '@/api/types/ProviderApi'
import { Glyph } from '@/components/svgs/Glyph'
import { colors } from '@/constants/colors'
import { addVisitToCalendar } from '@/features/scheduling/calendar'
import { PhotoFade } from '@/ui/core/block'
import { Button, ButtonText } from '@/ui/core/button'
import { FlowHeader } from '@/ui/core/flow-header'
import { Header } from '@/ui/core/header'
import { Pressable } from '@/ui/core/pressable'
import { VStack } from '@/ui/core/stack'
import { Text } from '@/ui/core/typography'
import { format } from 'date-fns'
import { Image } from 'expo-image'
import { useState } from 'react'
import { StyleSheet, View } from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import Toast from 'react-native-toast-message'
import Images from '#/assets/images/image-map'

/** Where the face sits once the frame is a phone rather than the photograph's own. */
const CROP = { top: '22%' as const }

/**
 * The one screen in this flow reached by succeeding.
 *
 * It was a notice: a tag, a heading and a white card on the pink, which is the shape of a form's
 * receipt. Everything else in the app is a booking; this is the only screen that is an occasion,
 * and the brand keeps a photograph for exactly that.
 *
 * The photograph carries the feeling and the type carries the fact, so neither has to explain the
 * other — a small-caps line, then the when at fifty-four points, and nothing else. No mint badge
 * with a tick: that is a systems convention rather than this brand's, and the line above the date
 * says the same thing in the brand's own voice.
 *
 * No address either, and no note about the released time on a reschedule. Where the studio is
 * lives one tap away under the visit; that the old time went back is true of every reschedule
 * and so is news to nobody. The lower third of a photograph holds two lines of type well and
 * four badly, and both states now sit at exactly two.
 *
 * No rule under the wordmark, though the picker has one. That rule exists because the picker's
 * header and its page share the same pink; here the photograph is the separation, and a line
 * drawn across a face reads as a crop mark.
 *
 * It takes the insets itself rather than letting the screen apply them. A safe area applied by
 * the screen is a band of pink above the photograph and another below it — which is the one
 * thing a full-bleed image must not have. The picture runs under the clock and under the home
 * indicator; only the chrome and the button are held off them.
 */
export const BookedNotice = ({
	visit,
	isReschedule,
	onSeeVisit,
	onDone,
}: {
	visit: BookedVisit
	isReschedule: boolean
	onSeeVisit: () => void
	onDone: () => void
}) => {
	const insets = useSafeAreaInsets()
	const [saving, setSaving] = useState(false)

	/**
	 * This screen's toast comes down from the top, where the rest of the app's come up from the
	 * bottom.
	 *
	 * Not a preference: the button that raises it is the second-from-last thing on the screen,
	 * and a toast at the bottom lands squarely on "See Your Visit" — the other action, covered by
	 * the answer to the first. Every other flow in the app raises a toast from a page whose foot
	 * is empty, so the default stays where it is and this one screen asks for the other end.
	 *
	 * Below the wordmark rather than under the clock: the header is white type on a photograph
	 * and the toast is a dark panel, and one laid over the other reads as a mistake.
	 */
	const place = { position: 'top' as const, topOffset: insets.top + 56 }

	const addToCalendar = async () => {
		setSaving(true)
		const result = await addVisitToCalendar({
			...visit,
			name: visit.appointmentType.name,
		})
		setSaving(false)
		Toast.show(
			result === 'added'
				? { ...place, type: 'success', text1: 'Added to your calendar' }
				: {
						...place,
						type: 'error',
						text1:
							result === 'denied'
								? 'Wally needs calendar access to add it'
								: "We couldn't add it to your calendar",
				  }
		)
	}

	return (
		<View className="flex-1">
			<Image
				source={Images.BrandPhoto}
				style={StyleSheet.absoluteFill}
				contentFit="cover"
				contentPosition={CROP}
				transition={240}
				accessibilityLabel=""
			/>
			<PhotoFade from={0.3} to={0.88} start={0.24} />

			{/* The wordmark in white, as login draws it on a photograph, and the way out beside it. */}
			<View style={{ paddingTop: insets.top }}>
				<FlowHeader
					tone="photo"
					right={
						<Pressable
							onPress={onDone}
							accessibilityRole="button"
							accessibilityLabel="Close"
							hitSlop={16}
						>
							<Glyph name="x-light" size={22} color={colors.white} />
						</Pressable>
					}
				/>
			</View>

			<View className="flex-1" />
			<VStack
				className="px-5"
				gap={0}
				style={{ paddingBottom: Math.max(insets.bottom, 20) + 8 }}
			>
				<Text
					size="body2"
					weight="bold"
					tone="white"
					className="uppercase tracking-[0.07em]"
				>
					{isReschedule ? 'Moved — see you then' : 'See you then'}
				</Text>
				{/* Two lines, set solid: the day over the hour, which is the order they are asked in. */}
				<Header
					variant="h2"
					tone="white"
					numberOfLines={2}
					adjustsFontSizeToFit
					className="mt-3 text-[54px] leading-[54px]"
				>
					{`${format(visit.startTimeLocal, 'EEE, MMM d')}\n${format(
						visit.startTimeLocal,
						'h:mm a'
					)}`}
				</Header>
				<Button
					variant="blush"
					size="lg"
					fullWidth
					isLoading={saving}
					disabled={saving}
					onPress={addToCalendar}
					className="mt-7"
				>
					<ButtonText>Add to Calendar</ButtonText>
				</Button>
				{/* The second action, in the app's own treatment for one standing on a photograph. */}
				<Button variant="ghost" size="lg" fullWidth onPress={onSeeVisit} className="mt-3">
					<ButtonText className="text-white">See Your Visit</ButtonText>
				</Button>
			</VStack>
		</View>
	)
}
